import Close20 from '@carbon/icons-react/es/close/20';
// eslint-disable-next-line no-restricted-imports
import { Modal as AntModal } from 'antd';
import { ModalFuncProps, ModalProps } from 'antd/es/modal';
import _ from 'lodash';
import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { ProductiveHeading05 } from 'components/styles';
import { useAntdRlUISwitcher } from 'contexts/antd-rlui-switcher';
import { useTranslate } from 'hooks/use-translation';
import { IconShell } from '../icon-shell';
import * as styles from './modal.module.css';

const ModalCloseIcon: FC = () => {
  const isNew = useAntdRlUISwitcher();

  return <IconShell size={isNew ? 'small' : 'large'} icon={<Close20 />} />;
};

export const defaultModalProps: ModalFuncProps = {
  okButtonProps: {
    className: styles.ok_button,
    type: 'primary',
  },
  cancelButtonProps: {
    className: styles.cancel_button,
  },
  closeIcon: <ModalCloseIcon />,
};

// Dont use merge function, it is costly and introduces performance issues
export const ModalBase = styled(AntModal).attrs(
  (props: ModalProps & ModalFuncProps) => {
    _.set(
      props,
      'okButtonProps.className',
      `${styles.ok_button} ${props?.okButtonProps?.className}`,
    );
    if (!props?.okButtonProps?.type) {
      _.set(props as ModalFuncProps, 'okButtonProps.type', 'primary');
    }
    if (!props?.okButtonProps?.['data-cy']) {
      _.set(props, 'okButtonProps.data-cy', 'modal.ok.button');
    }

    _.set(
      props,
      'cancelButtonProps.className',
      `${styles.cancel_button} ${props?.cancelButtonProps?.className}`,
    );
    if (!props?.cancelButtonProps?.['data-cy']) {
      _.set(props, 'cancelButtonProps.data-cy', 'modal.cancel.button');
    }

    if (!props?.closeIcon) {
      _.set(props, 'closeIcon', <ModalCloseIcon />);
    }

    return props;
  },
)`
  .ant-modal-header {
    padding: var(--spacing-05);
    border: none;
    :root[data-new-theme-enabled='true'] & {
      position: relative;
    }

    .ant-modal-title {
      ${ProductiveHeading05};
    }
  }
  .ant-modal-body {
    padding: 0 var(--spacing-05);
    width: 100%;
  }

  .ant-modal-footer {
    padding: var(--spacing-09) var(--spacing-05) var(--spacing-05);
    border: none;
  }

  .ant-modal-close {
    :root[data-new-theme-enabled='true'] & {
      position: absolute;
      top: 14px;
      right: var(--spacing-05);
    }
  }

  .ant-modal-close-x {
    :root[data-new-theme-enabled='true'] & {
      width: 32px;
      height: 32px;
    }
    width: 48px;
    height: 48px;
    &:hover {
      background-color: var(--hover-color);
      :root[data-new-theme-enabled='true'] & {
        border-radius: 8px;
      }
    }
  }
`;

export const Modal = (props: ModalProps & ModalFuncProps) => {
  const t = useTranslate();
  const { cancelText: _cancelText, okText: _okText, ...rest } = props;
  const cancelText = useMemo(
    () => (_cancelText !== undefined ? _cancelText : <>{t('cancel')}</>),
    [_cancelText, t],
  );

  const okText = useMemo(
    () => (_okText !== undefined ? _okText : <>{t('ok')}</>),
    [_okText, t],
  );

  return <ModalBase {...rest} cancelText={cancelText} okText={okText} />;
};
